<script setup lang="ts">
// Registration interstitial (between quote display and additional info). New customers confirm an email
// and continue (Keycloak account created later); returning customers use the Keycloak login redirect.
import { ref, computed } from 'vue';
import { useRiskStore } from '@/stores/riskData';
import { storeToRefs } from 'pinia';

const emit = defineEmits<{ (e: 'nav-next'): void; (e: 'nav-back'): void }>();

const store = useRiskStore();
const { riskData } = storeToRefs(store);

const quoteEmail = computed<string>(() => riskData.value?.InsuredEmailAddress ?? '');
const email = ref<string>(quoteEmail.value);
const error = ref('');
const submitting = ref(false);

const loginUrl = computed(() => `/account/login?returnUrl=${encodeURIComponent('/mypolicy/index')}`);

const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

const onContinue = async () => {
    error.value = '';
    const value = email.value.trim();
    if (!value) { error.value = 'Email address is required.'; return; }
    if (!isValidEmail(value)) { error.value = 'Please enter a valid email address.'; return; }

    submitting.value = true;
    try {
        const res = await fetch('/retail-shops/registercustomer', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ Email: value, InsuredEmailAddress: quoteEmail.value }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.success === false) {
            error.value = data?.error || 'A user with this email already exists. Please log in.';
            return;
        }
        emit('nav-next');
    } catch {
        error.value = 'Something went wrong. Please try again.';
    } finally {
        submitting.value = false;
    }
};
</script>

<template>
    <div class="quote-registration">
        <div class="ssq-main reg-main">
            <p class="reg-intro">
                If you have registered with Markel Direct before, please log in below. If you are new to
                Markel Direct, provide a username to register with later.
            </p>

            <div class="reg-cols">
                <!-- New customer: confirm the email, continue (account created later). -->
                <section class="reg-col">
                    <h2 class="reg-h">I'm a new customer</h2>
                    <div class="reg-fieldset">
                        <label class="reg-label" for="reg-email">Email address</label>
                        <input id="reg-email" type="email" class="reg-input" v-model="email"
                            :class="{ 'is-invalid': !!error }" :disabled="submitting"
                            placeholder="Email address" @keydown.enter.prevent="onContinue" />
                        <p class="reg-hint">You'll have the option of creating an account later.</p>
                    </div>
                    <p v-if="error" class="reg-error">{{ error }}</p>
                    <div class="reg-actions">
                        <button type="button" class="btn pri" :disabled="submitting" @click="onContinue">
                            {{ submitting ? 'Please wait…' : 'Continue →' }}
                        </button>
                    </div>
                </section>

                <div class="reg-or"><span>OR</span></div>

                <!-- Returning customer: real Keycloak login redirect (no inline password). -->
                <section class="reg-col">
                    <h2 class="reg-h">I'm already registered</h2>
                    <p class="reg-registered-sub">Log in to your account.</p>
                    <div class="reg-actions">
                        <a class="btn pri reg-login-full" :href="loginUrl">Customer login →</a>
                    </div>
                </section>
            </div>

            <div class="reg-back">
                <button type="button" class="btn out" @click="emit('nav-back')">← Back</button>
            </div>
        </div>
    </div>
</template>
